import { Box, Heading } from '@ksyos/design-system';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { HardwareProcessStatus } from '@common/enums/hardwareProcessStatus';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { employmentHasAccessToHolterDashboard } from '@frontend/services/api/accessCheckApiService';
import { createDesktopProcess, getDesktopProcess } from '@frontend/services/api/desktopProcessApiService';
import { getProcessActivityCustomControlData } from '@frontend/services/api/processActivitiesApiService';
import { getRequestProcedure } from '@frontend/services/api/requestApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { IReduxState } from '../../../configureStore';
import type { IHolterDesktopProcess } from '../../interfaces/holterDesktopProcess';
import { DesktopProcessActions } from '../../../../common/enums/desktopProcessActions';
import { HolterPhase } from '../../../../common/enums/holterPhase';
import { RecorderType } from '../../../../common/enums/recorderType';
import { ServiceCode } from '../../../../common/enums/serviceCode';
import { getSelectedLanguage } from '../../../translate';
import { CountdownButton } from '../../elements/CountdownButton';
import { openKsyosUrl, OpenKsyosUrlResult } from '../../elements/openKsyosUrl';
import { NotFoundError } from '../errors/NotFoundError';
import { HolterClearConfirmModal } from './HolterClearConfirmModal';
import { HolterClearModal } from './HolterClearModal';
import { HolterDashBoardCurrentHolter } from './HolterDashBoardCurrentHolter';
import { HolterProceduresAndProcessesList } from './holterProceduresList/HolterProceduresAndProcessesList';
import { HolterReadAndUploadDairyModel } from './holterReadAndUploadDairyModel';
import { KsyosUrlNotSupportedModal } from './KsyosUrlNotSupportedModal';

type DesktopParams = {
    [key: string]: string | undefined;
    desktopProcessId: string;
};

const timeoutIntervalSeconds = 1;
const timeoutIntervalMilliseconds = timeoutIntervalSeconds * 1000;
const maxWaitTimeSeconds = 60;

export function HolterDashboard() {
    const abortSignal = useAbortSignal();
    const translate = getSelectedLanguage();

    const selectedEmploymentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);

    let timeout: number;
    let timeWaited = 0;

    const [disableTimer, setDisableTimer] = useState(0);
    const [hasDesktopProcessTimedOut, setHasDesktopProcessTimedOut] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [noKsyosHandler, setNoKsyosHandler] = useState(false);
    const [showClearAndCancelExaminationModal, setShowClearAndCancelExaminationModal] = useState(false);
    const [showClearModal, setShowClearModal] = useState(false);
    const [showKsyosUrlNotSupportedModal, setShowKsyosUrlNotSupportedModal] = useState(false);
    const [showReadModal, setShowReadModal] = useState(false);
    const [hasAccessHolterDashboard, setHasAccessHolterDashboard] = useState<boolean | undefined>(undefined);
    const [desktopProcess, setDesktopProcess] = useState<IHolterDesktopProcess | undefined>(undefined);
    const [recorderType, setRecorderType] = useState<RecorderType | undefined>(undefined);
    const [activeButton, setActiveButton] = useState<'read' | 'clear' | 'cancel' | 'detect' | undefined>(undefined);
    const [showDisableTimer, setShowDisableTimer] = useState(false);

    useEffect(() => {
        checkAccessHolterDashboard();
        detectHolter();

        return () => {
            window.clearTimeout(timeout);
        };
    }, []);

    if (hasAccessHolterDashboard === false) {
        return <NotFoundError />;
    } else if (hasAccessHolterDashboard === undefined) {
        return null;
    }

    return (
        <PageTemplate>
            {showReadModal && desktopProcess && renderHolterReadModal()}

            {showClearModal && renderHolterClearModal()}

            {showClearAndCancelExaminationModal && renderHolterClearAndCancelExaminationModal()}

            <KsyosUrlNotSupportedModal
                isOpen={showKsyosUrlNotSupportedModal}
                onClose={async () => setShowKsyosUrlNotSupportedModal(false)}
            />

            <Box
                alignItems={'center'}
                display={'flex'}
                flexWrap={'wrap'}
                justifyContent={'space-between'}
                marginBottom={7}
                marginTop={10}
            >
                <Heading size="xlarge">{translate.holter.ecgRecordersOverview}</Heading>
                <Box
                    display={'flex'}
                    justifyContent={'flex-end'}
                    marginTop={1}
                    width={['100%', null, '50%']}
                >
                    {renderCountdownButton('detect', translate.holter.detectRecorder, async () => await detectHolter())}
                </Box>
            </Box>

            <HolterDashBoardCurrentHolter
                cancelButton={renderCountdownButton('cancel', translate.holter.cancelExamination, async () =>
                    setShowClearAndCancelExaminationModal(true)
                )}
                clearButton={renderCountdownButton('clear', translate.holter.clearRecorder, async () =>
                    setShowClearModal(true)
                )}
                desktopProcess={desktopProcess}
                disableTimer={showDisableTimer ? disableTimer : 0}
                hasDesktopProcessTimedOut={hasDesktopProcessTimedOut}
                noKsyosHandler={noKsyosHandler}
                readButton={renderCountdownButton(
                    'read',
                    translate.holter.startDownloading,
                    async () => await readHolter()
                )}
            />

            <HolterProceduresAndProcessesList />
        </PageTemplate>
    );

    function renderCountdownButton(
        button: 'read' | 'clear' | 'cancel' | 'detect',
        label: string,
        func: () => Promise<void>
    ) {
        return (
            <CountdownButton
                className={classnames(isProcessing || showDisableTimer ? 'disabled-button' : '', 'float-right')}
                duration={disableTimer * 1000}
                isLoading={activeButton === button && showDisableTimer}
                onClick={async () => {
                    setActiveButton(button);
                    await func();
                }}
            >
                {label}
            </CountdownButton>
        );
    }

    async function detectHolter() {
        setHasDesktopProcessTimedOut(false);
        await startProcessing(async () => {
            const params = {
                procedurePhase: HolterPhase.detect,
            };

            const result = await createDesktopProcess({
                action: DesktopProcessActions.detectHolter,
                params,
                serviceCode: ServiceCode.TCOH,
                signal: abortSignal,
            });

            const desktopParams = {
                ...params,
                apiUrl: location.protocol + '//' + location.host,
                desktopProcessId: result.id,
                employmentId: selectedEmploymentId,
                token: result.token,
            };

            await openUrlAndLoop(desktopParams);
        });
    }

    async function readHolter() {
        if (desktopProcess?.data) {
            await startProcessing(async () => {
                if (desktopProcess?.data) {
                    const params = {
                        holterPublicKey: desktopProcess.data.holterPublicKey,
                        holterSerialNumber: desktopProcess.data.holterSerialNumber,
                        holterToken: desktopProcess.data.holterToken,
                        preparationValue: desktopProcess.data.preparationValue,
                        procedurePhase: HolterPhase.read,
                        recorderType: desktopProcess.data.recorderType,
                    };

                    const result = await createDesktopProcess({
                        action: DesktopProcessActions.readHolter,
                        params,
                        processActivityId: desktopProcess.processActivityId,
                        requestId: desktopProcess.requestId,
                        serviceCode: desktopProcess.processActivityId ? ServiceCode.Holter : ServiceCode.TCOH,
                        signal: abortSignal,
                    });

                    const desktopParams: DesktopParams = {
                        ...params,
                        apiUrl: location.protocol + '//' + location.host,
                        desktopProcessId: result.id,
                        employmentId: selectedEmploymentId,
                        token: result.token,
                    };

                    await openUrlAndLoop(desktopParams);
                    await updateRecorderTypeForRequest();
                    await updateRecorderTypeForProcessActivity();
                    setShowReadModal(true);
                }
            });
        }
    }

    async function updateRecorderTypeForRequest() {
        if (desktopProcess?.requestId) {
            const procedureResult = await getRequestProcedure(desktopProcess.requestId, abortSignal);
            const customControlData = procedureResult.customControlData;
            setRecorderType(customControlData.recorderType ?? RecorderType.fysiologic);
        }
    }

    async function updateRecorderTypeForProcessActivity() {
        if (desktopProcess?.processActivityId) {
            const customControlData = await getProcessActivityCustomControlData(
                desktopProcess.processActivityId,
                abortSignal
            );
            setRecorderType(customControlData.recorderType ?? RecorderType.fysiologic);
        }
    }

    function renderHolterReadModal() {
        if (
            desktopProcess?.data &&
            (desktopProcess.requestId || desktopProcess.processActivityId) &&
            desktopProcess.data.downloadProgress &&
            recorderType
        ) {
            return (
                <HolterReadAndUploadDairyModel
                    downloadProgress={desktopProcess.data.downloadProgress}
                    onClose={async () => setShowReadModal(false)}
                    processActivityId={desktopProcess.processActivityId}
                    recorderType={recorderType}
                    requestId={desktopProcess.requestId}
                    showDownloadProgress
                />
            );
        }
    }

    async function clearHolter(procedurePhase: HolterPhase.clear | HolterPhase.clearRecording) {
        if (!desktopProcess?.data) {
            throw new Error('Missing desktopProcess or data');
        }

        await startProcessing(async () => {
            if (desktopProcess?.data) {
                const params: Partial<DesktopParams> = {
                    holterSerialNumber: desktopProcess.data.holterSerialNumber,
                    holterToken: desktopProcess.data.holterToken,
                    preparationValue: desktopProcess.data.preparationValue,
                    procedurePhase: procedurePhase,
                    recorderType: desktopProcess.data.recorderType,
                };

                if (desktopProcess.data.holterPublicKey) {
                    // As long as there are Ksyos Desktop versions out there that verify the holterPublicKey, we need to send it to Ksyos Desktop when available.
                    params.holterPublicKey = desktopProcess.data.holterPublicKey;
                }

                const result = await createDesktopProcess({
                    action: DesktopProcessActions.clearHolter,
                    params,
                    processActivityId: desktopProcess.processActivityId ?? undefined,
                    requestId: desktopProcess.requestId ?? undefined,
                    serviceCode: ServiceCode.TCOH,
                    signal: abortSignal,
                });

                const desktopParams: DesktopParams = {
                    ...params,
                    apiUrl: location.protocol + '//' + location.host,
                    desktopProcessId: result.id,
                    employmentId: selectedEmploymentId,
                    token: result.token,
                };

                await openUrlAndLoop(desktopParams);
                setShowClearModal(false);
                setShowClearAndCancelExaminationModal(false);
            }
        });
    }

    function renderHolterClearModal() {
        return (
            <HolterClearModal
                clearHolter={async () => await clearHolter(HolterPhase.clearRecording)}
                onClose={async () => setShowClearModal(false)}
            />
        );
    }

    function renderHolterClearAndCancelExaminationModal() {
        if (desktopProcess?.data && (desktopProcess.requestId || desktopProcess.processActivityId)) {
            let text = translate.holter.confirmationMessageClearHolter;
            let typeAction = HolterPhase.clear;

            if (desktopProcess.data.recordingsAvailable) {
                text = translate.holter.sureToClear;
                typeAction = HolterPhase.clearRecording;
            }

            return (
                <HolterClearConfirmModal
                    onNo={async () => setShowClearAndCancelExaminationModal(false)}
                    onYes={async () => {
                        await clearHolter(typeAction);
                    }}
                    text={text}
                />
            );
        }
    }

    async function openUrlAndLoop(params: DesktopParams) {
        timeWaited = 0;

        const url =
            'ksyos://holter/fysiologic?encoded=' + btoa(JSON.stringify(params)).replace(/\+/g, '-').replace(/\//g, '_');
        const openResult = await openKsyosUrl(url);

        if (openResult === OpenKsyosUrlResult.noHandler) {
            setNoKsyosHandler(true);
            await stopProcessing();
        } else if (openResult === OpenKsyosUrlResult.notSupportedByBrowser) {
            await stopProcessing();
            setShowKsyosUrlNotSupportedModal(true);
        } else {
            await pollDesktopProcess(params.desktopProcessId);
        }
    }

    async function pollDesktopProcess(desktopProcessId: string) {
        window.clearTimeout(timeout);
        try {
            const res = await getDesktopProcess(desktopProcessId, abortSignal);

            if (timeWaited >= maxWaitTimeSeconds && !res.data) {
                setHasDesktopProcessTimedOut(true);
                await stopProcessing();
            } else {
                timeWaited += timeoutIntervalSeconds;
            }

            if (res.data?.referToDesktopProcessId) {
                await pollDesktopProcess(res.data.referToDesktopProcessId);
            } else {
                setDesktopProcess(res);

                if (res.data?.error) {
                    await resetPolling();
                } else if (![HardwareProcessStatus.uploading, HardwareProcessStatus.completed].includes(res.status)) {
                    timeout = window.setTimeout(async () => {
                        await pollDesktopProcess(desktopProcessId);
                    }, timeoutIntervalMilliseconds);
                } else {
                    await stopProcessing();
                }
            }
        } catch (e: unknown) {
            await resetPolling(e as Error);
        }
    }

    async function checkAccessHolterDashboard() {
        const hasAccess = await employmentHasAccessToHolterDashboard(selectedEmploymentId, abortSignal);
        setHasAccessHolterDashboard(hasAccess);
    }

    async function resetPolling(error?: Error) {
        window.clearTimeout(timeout);
        timeWaited = 0;
        if (error && error.name === 'AbortError') {
            return;
        }
        await stopProcessing();
    }

    async function startProcessing(execute: () => Promise<void>) {
        setIsProcessing(true);

        if (disableTimer > 0) {
            setShowDisableTimer(true);
            const timeInterval = setInterval(async () => {
                if (disableTimer === 0) {
                    clearInterval(timeInterval);
                    setShowDisableTimer(false);
                    setActiveButton(undefined);
                    await execute();
                    await fireFoxDisableTimer();
                }
            }, 500);
        } else {
            await execute();
            await fireFoxDisableTimer();
        }
    }

    async function fireFoxDisableTimer() {
        if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
            setDisableTimer(10);
            setShowDisableTimer(false);
            const timeInterval = setInterval(async () => {
                if (disableTimer === 1) {
                    clearInterval(timeInterval);
                    setShowDisableTimer(false);
                    setDisableTimer(0);
                } else {
                    setDisableTimer(disableTimer - 1);
                }
            }, 1000);
        }
    }

    async function stopProcessing() {
        setIsProcessing(false);
    }
}
